// Copyright (c) 2026 Finerty Data Ltd. All rights reserved.
//
// This file is part of proprietary software owned by Finerty Data Ltd.
// It is confidential and may not be copied, distributed, modified, or used,
// in whole or in part, without the prior written consent of Finerty Data Ltd.
//
// Author: Ahmed Hosny

import React from 'react';
import './Alert.css';

export interface AlertProps {
  variant?: 'info' | 'success' | 'warning' | 'error';
  header?: string;
  dismissible?: boolean;
  onDismiss?: () => void;
  visible?: boolean;
  icon?: boolean;
  children?: React.ReactNode;
  className?: string;
  style?: React.CSSProperties;
}

const VARIANT_ICONS: Record<string, React.ReactNode> = {
  info: (
    <svg width="18" height="18" viewBox="0 0 18 18" fill="none" xmlns="http://www.w3.org/2000/svg" aria-hidden="true">
      <path d="M9 1.5C4.86 1.5 1.5 4.86 1.5 9C1.5 13.14 4.86 16.5 9 16.5C13.14 16.5 16.5 13.14 16.5 9C16.5 4.86 13.14 1.5 9 1.5ZM9.75 12.75H8.25V8.25H9.75V12.75ZM9.75 6.75H8.25V5.25H9.75V6.75Z" fill="currentColor" />
    </svg>
  ),
  success: (
    <svg width="18" height="18" viewBox="0 0 18 18" fill="none" xmlns="http://www.w3.org/2000/svg" aria-hidden="true">
      <path d="M9 1.5C4.86 1.5 1.5 4.86 1.5 9C1.5 13.14 4.86 16.5 9 16.5C13.14 16.5 16.5 13.14 16.5 9C16.5 4.86 13.14 1.5 9 1.5ZM7.5 12.75L3.75 9L4.8075 7.9425L7.5 10.6275L13.1925 4.935L14.25 6L7.5 12.75Z" fill="currentColor" />
    </svg>
  ),
  warning: (
    <svg width="18" height="18" viewBox="0 0 18 18" fill="none" xmlns="http://www.w3.org/2000/svg" aria-hidden="true">
      <path d="M1.5 15.75H16.5L9 3L1.5 15.75ZM9.75 13.5H8.25V12H9.75V13.5ZM9.75 10.5H8.25V7.5H9.75V10.5Z" fill="currentColor" />
    </svg>
  ),
  error: (
    <svg width="24" height="24" viewBox="0 0 24 24" fill="none" xmlns="http://www.w3.org/2000/svg" aria-hidden="true">
      <path d="M12 9V14" stroke="currentColor" strokeWidth="1.5" strokeLinecap="round" strokeLinejoin="round"/>
      <path d="M12 21.4093H5.93993C2.46993 21.4093 1.01993 18.9293 2.69993 15.8993L5.81993 10.2793L8.75993 4.99929C10.5399 1.78929 13.4599 1.78929 15.2399 4.99929L18.1799 10.2893L21.2999 15.9093C22.9799 18.9393 21.5199 21.4193 18.0599 21.4193H12V21.4093Z" stroke="currentColor" strokeWidth="1.5" strokeLinecap="round" strokeLinejoin="round"/>
      <path d="M11.9941 17H12.0031" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"/>
    </svg>
  ),
};

export function Alert({
  variant = 'info',
  header,
  dismissible = false,
  onDismiss,
  visible = true,
  icon = true,
  children,
  className = '',
  style,
}: AlertProps) {
  if (!visible) return null;

  const classes = [
    'fin-alert',
    `fin-alert--${variant}`,
    className,
  ]
    .filter(Boolean)
    .join(' ');

  return (
    <div className={classes} style={style} role="alert">
      {icon && (
        <span className="fin-alert__icon">{VARIANT_ICONS[variant]}</span>
      )}
      <div className="fin-alert__body">
        {header && <div className="fin-alert__header">{header}</div>}
        {children && <div className="fin-alert__content">{children}</div>}
      </div>
      {dismissible && (
        <button
          className="fin-alert__dismiss"
          type="button"
          aria-label="Dismiss alert"
          onClick={onDismiss}
        >
          <svg width="16" height="16" viewBox="0 0 16 16" fill="none" xmlns="http://www.w3.org/2000/svg" aria-hidden="true">
            <path
              d="M12 4L4 12M4 4L12 12"
              stroke="currentColor"
              strokeWidth="2"
              strokeLinecap="round"
            />
          </svg>
        </button>
      )}
    </div>
  );
}
